import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties
} from '../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../_shared/types/auction';
import ROUTES from '../../Routes/constants';
import { removePendingTrade } from '../../../redux/slices/pending';

import {
  AllowedStateChangeProperties,
  TradeState
} from '../../../_shared/types/trade';
import Alert from '../../../_shared/components/Alert';

import TradeListService from '../../../services/TradeListService';
import SendToClosingModal from '../../Modal/SendToClosingModal';
import TRToast from '../../../_shared/helpers/TRToast';
import UserService from '../../../services/UserService';
import ReducerConstants from '../../../_shared/constants/reducers';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

const SendToClosingContainer = ({
  trade,
  topBid,
  Button,
  associatedTrade,
  isInternalTrade,
  onStartNegotiation
}) => {
  const track = useAnalytics();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [sendToClosingModalIsOpen, setSendToClosingModalIsOpen] = useState(
    false
  );
  const [encourageModalLogic, setEncourageModalLogic] = useState([]);
  const [isEncourageModalOpen, setIsEncourageModalOpen] = useState(false);
  const userService = UserService.get();
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const tradeListService = TradeListService.get();
  const eventSource = window.location.href.includes(ROUTES.TRADE_URI)
    ? BidSource.VDP_OVERLAY
    : BidSource.LIST_VIEW;

  const sendTradeToClosing = async actualCashValue => {
    const moveSuccess = await tradeListService.moveToClosing(
      trade?.id,
      TradeState.pending,
      [{ key: AllowedStateChangeProperties.ACV, value: actualCashValue }]
    );

    TRToast(
      <Alert
        variant={moveSuccess && !moveSuccess.isError ? 'success' : 'error'}
        messages={[
          <FormattedMessage
            id={
              moveSuccess && !moveSuccess.isError
                ? 'modal.decision.sendToClosing.success'
                : moveSuccess.errorMessage
            }
          />
        ]}
      />
    );

    if (moveSuccess && !moveSuccess.isError) {
      track(SegmentConfig.names.pendingSendToClosingConfirmed, {
        ...getVinBasicProperties(trade),
        ...getSellerProperties(trade),
        event_state_type: SegmentConfig.eventStateType.sellPending
      });

      track(MixpanelConfig.names.sendToClosingFromPendingConfirm, {
        source: eventSource,
        trade_id: trade?.id,
        auction_id: trade?.auctionId
      });

      const isVdp = window.location.pathname.startsWith(ROUTES.TRADE_URI);
      if (isVdp) {
        setTimeout(() => window.location.reload(), 1000);
      } else {
        dispatch(removePendingTrade(trade?.id));
      }
    }
  };

  const onSendToClosing = async actualCashValue => {
    setIsLoading(true);
    await sendTradeToClosing(actualCashValue);
    setSendToClosingModalIsOpen(false);
    setIsLoading(false);
  };

  const onCloseSendToClosingModal = () => {
    setSendToClosingModalIsOpen(false);
  };

  const onOpenSendToClosingModal = () => {
    setSendToClosingModalIsOpen(true);

    track(SegmentConfig.names.pendingSendToClosingClicked, {
      ...getVinBasicProperties(trade),
      ...getSellerProperties(trade),
      event_state_type: userService.isSeller(user, trade?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending,
      event_source_type: window.location.href.includes(ROUTES.TRADE_URI)
        ? SegmentConfig.eventSourceType.pendingVDP
        : SegmentConfig.eventSourceType.pendingListView
    });
    track(MixpanelConfig.names.sendToClosingFromPending, {
      source: eventSource,
      trade_id: trade?.id,
      auction_id: trade?.auctionId
    });
  };

  const openEncourageModal = () => {
    const actionLabel = intl.formatMessage({
      id: 'negotiation.send.to.closing'
    });
    setEncourageModalLogic([
      {
        msg: (
          <FormattedMessage
            id="negotiation.encourage.modal.message"
            values={{ br: <br /> }}
          />
        ),
        title: (
          <FormattedMessage
            id="negotiation.encourage.modal.heading"
            values={{ actionLabel }}
          />
        ),
        confirmLabel: (
          <FormattedMessage id="vdp.pending.seller.button.startNegotiation" />
        ),
        cancelLabel: actionLabel,
        onCancel: () => () => sendTradeToClosing(trade?.topBid),
        onConfirm: () => () => {
          setIsEncourageModalOpen(false);
          onStartNegotiation();
        },
        onClickAway: () => () => setIsEncourageModalOpen(false)
      }
    ]);

    if (isOmp) {
      track(SegmentConfig.names.pendingUnusedProxyAlertShown, {
        ...getVinBasicProperties(trade),
        ...getSellerProperties(trade),
        event_state_type: SegmentConfig.eventStateType.sellPending
      });
    } else {
      track(MixpanelConfig.names.sellerUnusedProxyModalShown, {
        trade_id: trade?.id,
        auction_id: trade?.auctionId,
        user_type: MixpanelConfig.userType.seller,
        max_bid_in_auction: trade?.topBid
      });
    }

    setIsEncourageModalOpen(true);
  };

  const onClick = () => {
    if (trade?.unusedProxyExists) {
      openEncourageModal();
    } else {
      onOpenSendToClosingModal();
    }
  };

  return (
    <>
      <Button onClick={onClick} />
      <SendToClosingModal
        modalIsOpen={sendToClosingModalIsOpen}
        onSendToClosing={onSendToClosing}
        onClose={onCloseSendToClosingModal}
        topBid={topBid}
        associatedTrade={associatedTrade}
        associatedTradeId={trade?.associatedTradeId}
        isInternalTrade={isInternalTrade}
        isLoading={isLoading}
        trade={trade}
      />
      <ConfirmModal
        modalLogic={encourageModalLogic}
        isOpen={isEncourageModalOpen}
        isLoading={isLoading}
        loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
        width={540}
      />
    </>
  );
};

export default SendToClosingContainer;
